import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { TooltipModule } from "primeng/tooltip";
import { SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { InputNumberModule } from "primeng/inputnumber";
import { ButtonModule } from "primeng/button";
import { ChipModule } from "primeng/chip";
import { ToastService } from "../../../../../core/services/toast.service";
import {
  CustomerPortalSettingsForm,
  QPilotFrequencyTypeValues,
} from "../../../interfaces/customer-portal-settings.interface";

@Component({
  selector: "app-general-settings",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CardModule,
    CheckboxModule,
    TooltipModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    ChipModule,
  ],
  template: `
    <div class="space-y-6">
      <!-- Customer Options Card -->
      <p-card>
        <ng-template pTemplate="title">
          <div class="flex items-center gap-2">
            <i class="pi pi-cog text-blue-600"></i>
            General Settings
            <i
              class="pi pi-info-circle text-gray-400 cursor-pointer"
              pTooltip="Manage customer abilities for modifying their Scheduled Orders"
              tooltipPosition="right"
              [style.font-size]="'0.875rem'"
            ></i>
          </div>
        </ng-template>
        <ng-template pTemplate="subtitle">
          Manage the abilities that customers have when making changes to their
          Scheduled Orders. These settings apply to any Customer Portal display
          option.
        </ng-template>
        <ng-template pTemplate="content">
          <!-- Two Column Layout: Customer Options (60%) | Empty Space (40%) -->
          <div class="settings-grid">
            <!-- Left Column: Customer Options -->
            <div class="left-column">
              <h4 class="section-header">Customer Options</h4>

              <div class="option-item">
                <div class="option-content">
                  <p-checkbox
                    [formControl]="formGroup.controls.isShowingNotes"
                    [binary]="true"
                    inputId="showingNotes"
                  />
                  <div class="option-text">
                    <label for="showingNotes" class="option-label">
                      Customers can update Order Notes on their Scheduled Orders
                    </label>
                  </div>
                </div>
              </div>

              <div class="option-item">
                <div class="option-content">
                  <p-checkbox
                    [formControl]="formGroup.controls.isShowCoupons"
                    [binary]="true"
                    inputId="showCoupons"
                  />
                  <div class="option-text">
                    <label for="showCoupons" class="option-label">
                      Customers can add or remove Coupons on their Scheduled
                      Orders
                    </label>
                  </div>
                </div>
              </div>

              <div class="option-item">
                <div class="option-content">
                  <p-checkbox
                    [formControl]="formGroup.controls.allowRetryScheduledOrder"
                    [binary]="true"
                    inputId="retryOrders"
                  />
                  <div class="option-text">
                    <label for="retryOrders" class="option-label">
                      Customers can retry processing Failed Scheduled Orders
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: Frequency Options -->
            <div class="right-column">
              <h4 class="section-header">Frequency Options</h4>

              <div class="option-item">
                <div class="option-content">
                  <p-checkbox
                    [formControl]="formGroup.controls.manageAllowedFrequencies"
                    [binary]="true"
                    inputId="manageFrequencies"
                  />
                  <div class="option-text">
                    <label for="manageFrequencies" class="option-label">
                      Manage Allowed Frequencies
                    </label>
                  </div>
                </div>
              </div>

              <div
                *ngIf="formGroup.controls.manageAllowedFrequencies.value"
                class="frequency-selector"
              >
                <div class="frequency-inputs">
                  <div class="frequency-type">
                    <label for="frequencyType" class="frequency-input-label"
                      >Frequency Type</label
                    >
                    <p-select
                      [(ngModel)]="frequencyType"
                      [options]="frequencyTypes"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select Type"
                      [style]="{ width: '100%' }"
                      inputId="frequencyType"
                      appendTo="body"
                    />
                  </div>

                  <div class="frequency-value">
                    <label for="frequencyValue" class="frequency-input-label">
                      {{
                        frequencyType === "DayOfTheWeek"
                          ? "Day of the Week"
                          : "Value"
                      }}
                    </label>
                    <p-select
                      *ngIf="frequencyType === 'DayOfTheWeek'"
                      [(ngModel)]="frequencyValue"
                      [options]="daysOfWeek"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select Day"
                      [style]="{ width: '100%' }"
                      inputId="frequencyValue"
                      appendTo="body"
                    />
                    <p-inputNumber
                      *ngIf="frequencyType !== 'DayOfTheWeek'"
                      [(ngModel)]="frequencyValue"
                      [min]="1"
                      [showButtons]="true"
                      [useGrouping]="false"
                      placeholder="Only numbers allowed"
                      inputStyleClass="frequency-text-input w-full"
                      inputId="frequencyNumericValue"
                      autocomplete="off"
                    />
                  </div>

                  <p-button
                    label="Add"
                    [disabled]="!frequencyType || !frequencyValue"
                    (onClick)="addFrequency()"
                    size="small"
                  />
                </div>

                <div
                  *ngIf="allowedFrequenciesArray.length > 0"
                  class="frequency-chips"
                >
                  <p-chip
                    *ngFor="
                      let freq of allowedFrequenciesArray.controls;
                      let i = index
                    "
                    [label]="getFrequencyDisplay(freq.value)"
                    [removable]="true"
                    (onRemove)="removeFrequency(i)"
                    styleClass="frequency-chip"
                  />
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </p-card>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: fit-content;
        min-height: auto;
      }

      .section-header {
        color: #495057;
        margin-bottom: 1.5rem;
        font-size: 1.1rem;
        font-weight: 600;
      }

      .option-item {
        padding: 0.75rem;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        margin-bottom: 0.75rem;
        background-color: #f8f9fa;
        transition: all 0.2s ease;
        min-height: 60px;
        display: flex;
        align-items: center;
      }

      .option-item:hover {
        background-color: #e9ecef;
        border-color: #dee2e6;
      }

      .option-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
      }

      .option-text {
        flex: 1;
      }

      .option-label {
        display: block;
        cursor: pointer;
        font-weight: 500;
        color: #374151;
        line-height: 1.5;
        margin-bottom: 0;
      }

      .option-description {
        display: block;
        color: #6b7280;
        font-size: 0.875rem;
        margin-top: 0.5rem;
        line-height: 1.4;
      }

      :host ::ng-deep .p-card {
        height: fit-content;
        box-shadow:
          0 1px 3px 0 rgb(0 0 0 / 0.1),
          0 1px 2px -1px rgb(0 0 0 / 0.1);
        border: 1px solid #e5e7eb;
        border-radius: 8px;
      }

      :host ::ng-deep .p-card .p-card-header {
        border-bottom: 1px solid #e5e7eb;
        padding: 1.25rem;
      }

      :host ::ng-deep .p-card .p-card-content {
        padding: 1.25rem;
      }

      :host ::ng-deep .p-card .p-card-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
      }

      :host ::ng-deep .p-card .p-card-subtitle {
        color: #6b7280;
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }

      :host ::ng-deep .p-checkbox .p-checkbox-box {
        border-radius: 4px;
      }

      .mt-4 {
        margin-top: 1.5rem;
      }

      .settings-grid {
        display: grid;
        grid-template-columns: 60% 40%;
        gap: 1.5rem;
        margin-bottom: 1.5rem;
      }

      .left-column {
        display: flex;
        flex-direction: column;
      }

      .right-column {
        display: flex;
        flex-direction: column;
      }

      .frequency-selector {
        margin-top: 0.75rem;
      }

      .frequency-inputs {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .frequency-type,
      .frequency-value {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .frequency-input-label {
        font-weight: 500;
        color: #374151;
        font-size: 0.875rem;
      }

      .frequency-text-input {
        width: 100%;
      }

      .frequency-chips {
        margin-top: 1rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      :host ::ng-deep .frequency-chip {
        background-color: #f3f4f6;
        color: #374151;
        border: 1px solid #e5e7eb;
      }

      :host ::ng-deep .frequency-chip .p-chip-remove-icon {
        color: #6b7280;
        transition: color 0.15s ease;
      }

      :host ::ng-deep .frequency-chip .p-chip-remove-icon:hover {
        color: #374151;
      }

      @media (max-width: 768px) {
        .settings-grid {
          grid-template-columns: 1fr;
          gap: 1rem;
        }
      }
    `,
  ],
})
export class GeneralSettingsComponent {
  @Input({ required: true }) formGroup!: FormGroup<CustomerPortalSettingsForm>;
  @Input({ required: true }) isLoading!: boolean;

  frequencyValue: number | null = null;
  frequencyType: string = "";

  frequencyTypes = [
    { label: "Days", value: "Days" },
    { label: "Weeks", value: "Weeks" },
    { label: "Months", value: "Months" },
    { label: "Day of the Week (1-7)", value: "DayOfTheWeek" },
    { label: "Day of the Month", value: "DayOfTheMonth" },
  ];

  daysOfWeek = [
    { label: "Sunday", value: 1 },
    { label: "Monday", value: 2 },
    { label: "Tuesday", value: 3 },
    { label: "Wednesday", value: 4 },
    { label: "Thursday", value: 5 },
    { label: "Friday", value: 6 },
    { label: "Saturday", value: 7 },
  ];

  constructor(private toastService: ToastService) {}

  get allowedFrequenciesArray(): FormArray<
    FormControl<QPilotFrequencyTypeValues>
  > {
    return this.formGroup.controls.allowedFrequencies;
  }

  addFrequency(): void {
    if (this.frequencyValue === null || !this.frequencyType) {
      return;
    }

    const numValue = this.frequencyValue;
    if (numValue < 1) {
      this.toastService.showError(
        "Error",
        "Please enter a valid number greater than 0",
      );
      return;
    }

    // Validation for Day of the Week (must be between 1 and 7)
    if (
      this.frequencyType === "DayOfTheWeek" &&
      (numValue < 1 || numValue > 7)
    ) {
      this.toastService.showError(
        "Error",
        "Day of the Week must be between 1 and 7",
      );
      return;
    }

    // Validation for Day of the Month (must be between 1 and 31)
    if (
      this.frequencyType === "DayOfTheMonth" &&
      (numValue < 1 || numValue > 31)
    ) {
      this.toastService.showError(
        "Error",
        "Day of the Month must be between 1 and 31 (months do not have more than 31 days)",
      );
      return;
    }

    const newFrequency: QPilotFrequencyTypeValues = {
      FrequencyType: this.frequencyType,
      Value: numValue,
      DisplayName: null,
    };

    // Check if frequency already exists
    const currentValues = this.allowedFrequenciesArray
      .value as QPilotFrequencyTypeValues[];
    const exists = currentValues.some(
      (f: QPilotFrequencyTypeValues) =>
        f.Value === numValue && f.FrequencyType === this.frequencyType,
    );

    if (!exists) {
      // Add new frequency to the FormArray
      this.allowedFrequenciesArray.push(
        new FormControl(newFrequency, { nonNullable: true }),
      );

      // Reset inputs
      this.frequencyValue = null;
      this.frequencyType = "";
    } else {
      this.toastService.showError("Error", "This frequency already exists");
    }
  }

  removeFrequency(index: number): void {
    this.allowedFrequenciesArray.removeAt(index);
  }

  getFrequencyDisplay(freq: QPilotFrequencyTypeValues): string {
    // Day of the Week - show just the day name (e.g., "Tuesday")
    if (freq.FrequencyType === "DayOfTheWeek") {
      const dayNames = [
        "",
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      return dayNames[freq.Value] || `Day ${freq.Value}`;
    }

    // Day of the Month - show with ordinal suffix (e.g., "5th of Month")
    if (freq.FrequencyType === "DayOfTheMonth") {
      return `${this.getOrdinal(freq.Value)} of Month`;
    }

    // Handle singular vs plural (e.g., "1 Day" vs "2 Days")
    const typeLabel =
      freq.Value === 1
        ? freq.FrequencyType.replace(/s$/, "")
        : freq.FrequencyType;
    return `${freq.Value} ${typeLabel}`;
  }

  private getOrdinal(n: number): string {
    const suffixes = ["th", "st", "nd", "rd"];
    const remainder = n % 100;
    const suffix =
      suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
    return `${n}${suffix}`;
  }
}
